// PGN import (plan Item B3), built on chessops's DoS-resistant PGN parser. Turns
// pasted / uploaded PGN text (one or many games) into the app's move model: the
// authoritative UCI list + an optional starting FEN + header-derived `PgnMeta`.
// Mainline only in v1 — variations and NAGs are ignored (they don't affect the
// played line); `{comments}` are kept per move (plan B1). Pure and sandboxed
// (chessops has no eval); ply/game caps keep a pathological paste from hanging
// the tab.

import { emptyHeaders, parsePgn, startingPosition } from "chessops/pgn";
import { INITIAL_FEN, makeFen } from "chessops/fen";
import { parseSan } from "chessops/san";
import { makeUci } from "chessops/util";
import type { PgnMeta } from "./pgn.ts";

export interface ImportedGame {
  moves: { uci: string; comment?: string }[];
  initialFen?: string;
  meta: PgnMeta;
}

/** The outcome of importing a (possibly multi-game) PGN blob. */
export interface ImportResult {
  games: ImportedGame[];
  /** How many game blocks with real content were seen (imported + failed). */
  total: number;
}

const MAX_GAMES = 500; // guard a pathological multi-game paste
const MAX_PLIES = 600; // ~300 full moves — well past any real game

/**
 * Parse `pgnText` into games. Lenient: a block whose starting position can't be
 * built is skipped (counted in `total` but absent from `games`); an illegal move
 * mid-line truncates that game to its legal prefix. Blank/garbage blocks with no
 * headers and no moves are ignored entirely.
 */
export function parseGames(pgnText: string): ImportResult {
  // `emptyHeaders` (not the default seven-tag roster) so a pure-garbage block
  // stays header-less and is recognisably empty.
  const parsed = parsePgn(pgnText, emptyHeaders);
  const games: ImportedGame[] = [];
  let total = 0;

  for (const game of parsed) {
    const firstNode = game.moves.children[0];
    const hasContent = game.headers.size > 0 || firstNode !== undefined;
    if (!hasContent) continue; // blank trailing block / stray text
    total++; // count every real block, even past the cap, so `skipped` is honest

    // Past the game cap: keep counting (above) but stop the expensive parse, so a
    // huge paste reports "N could not be parsed" instead of silently swallowing them.
    if (games.length >= MAX_GAMES) continue;

    const pos = startingPosition(game.headers).unwrap(
      (p) => p,
      () => null,
    );
    if (!pos) continue; // unparseable start (bad FEN) → counted as failed

    const startFen = makeFen(pos.toSetup());
    const moves: { uci: string; comment?: string }[] = [];
    for (const node of game.moves.mainline()) {
      if (moves.length >= MAX_PLIES) break;
      const move = parseSan(pos, node.san);
      if (!move) break; // illegal → keep the legal prefix
      // Keep the move's annotation text, minus embedded `[%cal …]`-style command
      // tags (lichess arrows/clocks), whitespace-normalised and capped.
      const raw = (node.comments ?? []).join(" ");
      const comment =
        raw
          .replace(/\[%[^\]]*\]/g, "")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 300) || undefined;
      moves.push({ uci: makeUci(move), ...(comment ? { comment } : {}) });
      pos.play(move);
    }

    games.push({
      moves,
      initialFen: startFen !== INITIAL_FEN ? startFen : undefined,
      meta: metaFromHeaders(game.headers),
    });
  }

  return { games, total };
}

/** Build a `PgnMeta` from PGN headers (only the fields we round-trip). */
function metaFromHeaders(h: Map<string, string>): PgnMeta {
  const g = (k: string) => h.get(k) || undefined;
  return {
    event: g("Event"),
    site: g("Site"),
    date: g("Date"),
    round: g("Round"),
    white: g("White") ?? "?",
    black: g("Black") ?? "?",
    result: g("Result") ?? "*",
    whiteElo: g("WhiteElo"),
    blackElo: g("BlackElo"),
    timeControl: g("TimeControl"),
    eco: g("ECO"),
    opening: g("Opening"),
    termination: g("Termination"),
  };
}
